import { Box, Button, SubPageLayout, useSubPage } from "@bcc-ui/ui-components";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";

import ManageRoleSubpageForm, { FormValues, MANAGE_ROLE_FORM_ID } from "./ManageRoleSubpageForm";
import { Authority, Targets } from "domain/roles";

interface Props {
    name: string;
    description: string;
    authorities: Authority[];
    targets?: Targets;
    isManager: boolean;
    isEditing?: boolean;
    submitEventHandler?: (values: FormValues) => Promise<void>;
    isViewRole: boolean;
}

export const ManageRoleViewSubpage = (props: Props) => {
    const { t } = useTranslation();
    const formRef = React.useRef<FormikProps<FormValues>>(null);
    const [resetCounter, setResetCounter] = React.useState<number>(0);
    const [hasError, setHasError] = React.useState(false);
    const { closeSubPage } = useSubPage();

    const resetForm = () => {
        formRef.current?.resetForm();
        setResetCounter((prev) => prev + 1);
    };

    React.useEffect(() => () => closeSubPage(), [closeSubPage]);

    return (
        <SubPageLayout
            title={
                props.isEditing
                    ? t("ManageRoleForm.edit.editRoleTitle")
                    : props.isViewRole
                      ? t("RoleDetails.title", { name: props.name })
                      : t("ManageRoleForm.createRoleTitle")
            }

            buttons={
                props.isViewRole ? undefined : (
                    <>
                        {props.isEditing && (
                            <Box verticalAlignment="Center">
                                <Button variant="Tertiary" type="button" onClick={resetForm}>
                                    {t("ManageRoleForm.edit.resetButton")}
                                </Button>
                            </Box>
                        )}
                        <Button variant="Primary" type="submit" form={MANAGE_ROLE_FORM_ID}>
                            {props.isEditing ? t("Common.save") : t("Common.create")}
                        </Button>
                    </>
                )
            }
            cancelButtonLabel={t("Common.cancel")}
            footerBanner={{
                visible: hasError,
                text: t("Common.formValidationError"),
            }}
        >
            <ManageRoleSubpageForm
                name={props.name}
                authorities={props.authorities}
                description={props.description}
                targets={props.targets}
                submitEventHandler={props.submitEventHandler}
                isManager={props.isManager}
                isEditing={props.isEditing}
                isViewRole={props.isViewRole}
                resetCounter={resetCounter}
                formRef={formRef}
                onErrorChange={setHasError}
            />
        </SubPageLayout>
    );
};
